import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SingleValue } from 'react-select';

import { Button, FormField, Input, Select } from '@revelio/ui';

import type {
  DeliveryFormat,
  SftpDeliveryConfig,
} from '../../data-builder.store';

type SftpConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValue: SftpDeliveryConfig | null;
  onSave: (value: SftpDeliveryConfig) => void;
};

type SftpConfigForm = {
  path: string;
  format: DeliveryFormat;
};

const formatOptions: { value: DeliveryFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'excel', label: 'Excel' },
];

export const SftpConfigModal = ({
  isOpen,
  onClose,
  initialValue,
  onSave,
}: SftpConfigModalProps) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SftpConfigForm>({
    defaultValues: {
      path: initialValue?.path ?? '',
      format: initialValue?.format ?? 'csv',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      path: initialValue?.path ?? '',
      format: initialValue?.format ?? 'csv',
    });
  }, [initialValue, isOpen, reset]);

  const onSubmit = handleSubmit((value) => {
    onSave(value);
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">SFTP Configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormField
            label="Path"
            errors={
              errors.path?.message !== undefined
                ? [errors.path.message]
                : undefined
            }
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="Path (e.g. /uploads/databuilder_output)"
              {...register('path', {
                required: 'Path is required',
                minLength: {
                  value: 1,
                  message: 'Minimum length should be 1',
                },
                pattern: {
                  value: /^[a-zA-Z0-9_\-./]+$/,
                  message:
                    'Path can only contain letters, numbers, underscores, hyphens, periods, and forward slashes',
                },
              })}
            />
          </FormField>

          <FormField
            label="Format"
            errors={
              errors.format?.message !== undefined
                ? [errors.format.message]
                : undefined
            }
          >
            <Controller
              control={control}
              name="format"
              rules={{ required: 'Format is required' }}
              render={({ field }) => (
                <Select
                  options={formatOptions}
                  value={formatOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(
                    selectedOption: SingleValue<{
                      value: DeliveryFormat;
                      label: string;
                    }>
                  ) => {
                    field.onChange(selectedOption?.value ?? 'csv');
                  }}
                />
              )}
            />
          </FormField>
        </ModalBody>

        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={onSubmit} disabled={isSubmitting}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
